/**
 * @fileoverview Job management hooks using TanStack Query.
 *
 * Provides hooks for:
 * - Fetching jobs by tenant or technician
 * - Creating and updating jobs
 * - Job status management
 * - Real-time job updates
 *
 * @module hooks/use-jobs
 */

import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useCurrentTenant } from "./use-tenant";
import { toast } from "sonner";
import { findOrCreateCustomer, CUSTOMERS_QUERY_KEY } from "./use-customers";

// ============================================================================
// Types
// ============================================================================

export type JobStatus =
  | "unassigned"
  | "scheduled"
  | "confirmed"
  | "en_route"
  | "in_progress"
  | "completed"
  | "cancelled";

export type BookingSource = "ai_agent" | "manual" | "web_form" | "phone_transfer";

export interface Job {
  id: string;
  tenant_id: string;
  crm_job_id: string | null;
  customer_id: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  customer_address: string | null;
  technician_id: string | null;
  service_type: string | null;
  description: string | null;
  issue_description: string | null;
  status: JobStatus | null;
  booking_source: BookingSource;
  scheduled_at: string | null;
  en_route_at: string | null;
  arrived_at: string | null;
  completed_at: string | null;
  eta_minutes: number | null;
  eta_sent_at: string | null;
  eta_link_token: string | null;
  arrival_sms_sent_at: string | null;
  tech_phone: string | null;
  call_recording_url: string | null;
  external_calendar_event_id: string | null;
  notes: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Joined relations
  technician?: {
    id: string;
    name: string;
    phone: string | null;
  } | null;
  customer?: {
    id: string;
    name: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
  } | null;
}

export interface JobFilters {
  status?: JobStatus | "all";
  technicianId?: string;
  customerId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface CreateJobInput {
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
  customer_address?: string;
  service_type: string;
  description?: string;
  scheduled_at?: string;
  technician_id?: string;
  booking_source?: BookingSource;
  /** Optional: pre-selected customer ID (skips findOrCreateCustomer) */
  existing_customer_id?: string;
}

export interface UpdateJobInput {
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  customer_address?: string;
  service_type?: string;
  description?: string;
  scheduled_at?: string;
  technician_id?: string | null;
  status?: JobStatus;
  notes?: Record<string, unknown>;
  eta_minutes?: number;
}

// ============================================================================
// Query Keys
// ============================================================================

export const JOBS_QUERY_KEY = ["jobs"];

// ============================================================================
// API Functions
// ============================================================================

async function fetchJobs(
  tenantId: string,
  filters?: JobFilters
): Promise<Job[]> {
  if (!supabase) return [];

  let query = supabase
    .from("jobs")
    .select(`
      *,
      technician:technicians(id, name, phone)
    `)
    .eq("tenant_id", tenantId)
    .order("scheduled_at", { ascending: true });

  if (filters?.status && filters.status !== "all") {
    query = query.eq("status", filters.status);
  }

  if (filters?.technicianId) {
    query = query.eq("technician_id", filters.technicianId);
  }

  if (filters?.customerId) {
    query = query.eq("customer_id", filters.customerId);
  }

  if (filters?.dateFrom) {
    query = query.gte("scheduled_at", filters.dateFrom);
  }

  if (filters?.dateTo) {
    query = query.lte("scheduled_at", filters.dateTo);
  }

  if (filters?.search) {
    query = query.or(
      `customer_name.ilike.%${filters.search}%,customer_phone.ilike.%${filters.search}%,service_type.ilike.%${filters.search}%,customer_address.ilike.%${filters.search}%`
    );
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

async function fetchTechnicianJobs(
  tenantId: string,
  technicianId: string,
  dateFrom?: string,
  dateTo?: string
): Promise<Job[]> {
  if (!supabase) return [];

  let query = supabase
    .from("jobs")
    .select("*")
    .eq("tenant_id", tenantId)
    .eq("technician_id", technicianId)
    .order("scheduled_at", { ascending: true });

  if (dateFrom) {
    query = query.gte("scheduled_at", dateFrom);
  }

  if (dateTo) {
    query = query.lte("scheduled_at", dateTo);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

async function fetchJobById(tenantId: string, jobId: string): Promise<Job | null> {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from("jobs")
    .select(`
      *,
      technician:technicians(id, name, phone)
    `)
    .eq("id", jobId)
    .eq("tenant_id", tenantId)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }

  return data;
}

export interface CreateJobResult {
  job: Job;
  customerLinked: boolean;
  customerIsNew: boolean;
  customerId: string | null;
  /** Dispatch auto-assignment result */
  dispatchResult?: {
    autoAssigned: boolean;
    assignedTechId: string | null;
    suggestionCreated: boolean;
  };
}

async function createJob(
  tenantId: string,
  input: CreateJobInput
): Promise<CreateJobResult> {
  if (!supabase) throw new Error("Supabase client not initialized");

  let customerId: string | null = null;
  let customerIsNew = false;
  let customerLinked = false;

  // If a customer was pre-selected (e.g., from address match), use that ID directly
  if (input.existing_customer_id) {
    customerId = input.existing_customer_id;
    customerLinked = true;
    customerIsNew = false;
  }
  // Otherwise, if we have a phone number, find or create the customer
  else if (input.customer_phone) {
    try {
      const { customer, isNew } = await findOrCreateCustomer(tenantId, {
        name: input.customer_name,
        phone: input.customer_phone,
        email: input.customer_email,
        address: input.customer_address,
        created_source: input.booking_source === "ai_agent" ? "ai_agent" : "job_creation",
      });
      customerId = customer.id;
      customerIsNew = isNew;
      customerLinked = true;
    } catch (err) {
      // Log but don't fail job creation if customer linking fails
      if (import.meta.env.DEV) console.warn("Failed to link customer:", err);
    }
  }

  const { data, error } = await supabase
    .from("jobs")
    .insert({
      tenant_id: tenantId,
      customer_id: customerId,
      customer_name: input.customer_name,
      customer_phone: input.customer_phone,
      customer_email: input.customer_email,
      customer_address: input.customer_address,
      service_type: input.service_type,
      description: input.description,
      scheduled_at: input.scheduled_at,
      technician_id: input.technician_id,
      booking_source: input.booking_source || "manual",
      status: input.technician_id ? "scheduled" : "unassigned",
    })
    .select()
    .single();

  if (error) throw error;

  // Apply dispatch auto-assignment logic
  const dispatchResult = await applyDispatchLogic(
    tenantId,
    data.id,
    !!input.technician_id // Skip dispatch if tech was manually assigned
  );

  // If auto-assigned, update job object with new data
  let finalJob = data;
  if (dispatchResult.autoAssigned && dispatchResult.assignedTechId) {
    finalJob = {
      ...data,
      technician_id: dispatchResult.assignedTechId,
      status: "scheduled",
    };
  }

  return {
    job: finalJob,
    customerLinked,
    customerIsNew,
    customerId,
    dispatchResult: {
      autoAssigned: dispatchResult.autoAssigned,
      assignedTechId: dispatchResult.assignedTechId,
      suggestionCreated: !dispatchResult.autoAssigned && !input.technician_id,
    },
  };
}

async function updateJob(
  tenantId: string,
  jobId: string,
  updates: UpdateJobInput
): Promise<Job> {
  if (!supabase) throw new Error("Supabase client not initialized");

  const { data, error} = await supabase
    .from("jobs")
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq("id", jobId)
    .eq("tenant_id", tenantId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ============================================================================
// Dispatch Auto-Assignment
// ============================================================================

interface ActiveTechnician {
  id: string;
  name: string;
  is_active: boolean;
}

/**
 * Get active technicians for a tenant (for dispatch logic).
 * Used to determine if single-operator auto-assign or multi-tech suggestion is needed.
 */
async function getActiveTechnicians(tenantId: string): Promise<ActiveTechnician[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("technicians")
    .select("id, name, is_active")
    .eq("tenant_id", tenantId)
    .eq("is_active", true);

  if (error) {
    if (import.meta.env.DEV) console.warn("Failed to fetch technicians for dispatch:", error);
    return [];
  }

  return data || [];
}

/**
 * Create a dispatch suggestion for owner approval (multi-tech scenario).
 * AI-powered scoring: In MVP, uses simple scoring based on workload.
 */
async function createDispatchSuggestion(
  tenantId: string,
  jobId: string,
  technicians: ActiveTechnician[]
): Promise<void> {
  if (!supabase || technicians.length === 0) return;

  // MVP: Simple scoring - suggest first available tech
  // TODO: Enhance with specialty matching, location, route optimization
  const suggestedTech = technicians[0];

  // Calculate simple confidence score (higher for fewer choices)
  const confidenceScore = Math.min(95, 60 + (100 / technicians.length));

  // Build alternative suggestions
  const alternatives = technicians.slice(1, 4).map((tech, index) => ({
    technician_id: tech.id,
    technician_name: tech.name,
    total_score: confidenceScore - ((index + 1) * 10),
    reasoning: "Available technician",
  }));

  // Set expiration to 24 hours from now
  const expiresAt = new Date();
  expiresAt.setHours(expiresAt.getHours() + 24);

  const { error } = await supabase
    .from("job_assignment_suggestions")
    .insert({
      tenant_id: tenantId,
      job_id: jobId,
      suggested_technician_id: suggestedTech.id,
      confidence_score: confidenceScore,
      reasoning: `AI suggests ${suggestedTech.name} based on availability. Review and approve or select different technician.`,
      // Score constraints: specialty 0-40, location 0-30, route 0-20, workload 0-10
      score_specialty: 28, // Default - TODO: calculate from job service_type (max 40)
      score_location: 24, // Default - TODO: calculate from tech location (max 30)
      score_route: 15, // Default - TODO: calculate from existing schedule (max 20)
      score_workload: 8, // Default - TODO: calculate from today's job count (max 10)
      alternative_suggestions: alternatives,
      owner_approved: null, // Pending approval
      expires_at: expiresAt.toISOString(),
    });

  if (error) {
    if (import.meta.env.DEV) console.warn("Failed to create dispatch suggestion:", error);
    // Don't throw - job still created, just no suggestion
  }
}

/**
 * Apply dispatch auto-assignment logic after job creation.
 * - Single tech: Auto-assigns immediately
 * - Multi-tech: Creates suggestion for owner approval
 */
async function applyDispatchLogic(
  tenantId: string,
  jobId: string,
  hasManualAssignment: boolean
): Promise<{ autoAssigned: boolean; assignedTechId: string | null }> {
  // Skip if technician was manually assigned
  if (hasManualAssignment) {
    return { autoAssigned: false, assignedTechId: null };
  }

  const technicians = await getActiveTechnicians(tenantId);

  if (technicians.length === 0) {
    // No technicians - job stays unassigned
    return { autoAssigned: false, assignedTechId: null };
  }

  if (technicians.length === 1) {
    // Single operator mode: Auto-assign immediately
    const tech = technicians[0];

    const { error } = await supabase!
      .from("jobs")
      .update({
        technician_id: tech.id,
        status: "scheduled",
        updated_at: new Date().toISOString(),
      })
      .eq("id", jobId)
      .eq("tenant_id", tenantId);

    if (error) {
      if (import.meta.env.DEV) console.warn("Failed to auto-assign job:", error);
      return { autoAssigned: false, assignedTechId: null };
    }

    if (import.meta.env.DEV) console.log(`[Dispatch] Auto-assigned job ${jobId} to single tech ${tech.name}`);
    return { autoAssigned: true, assignedTechId: tech.id };
  }

  // Multi-tech mode: Create suggestion for owner approval
  await createDispatchSuggestion(tenantId, jobId, technicians);
  if (import.meta.env.DEV) console.log(`[Dispatch] Created suggestion for job ${jobId} (${technicians.length} techs available)`);

  return { autoAssigned: false, assignedTechId: null };
}

async function updateJobStatus(
  tenantId: string,
  jobId: string,
  status: JobStatus
): Promise<Job> {
  if (!supabase) throw new Error("Supabase client not initialized");

  // HIGH-12 FIX: Delete calendar event when job is cancelled
  // This prevents orphaned calendar events when jobs are cancelled instead of deleted
  if (status === "cancelled") {
    // First fetch the job to get external_calendar_event_id
    const { data: existingJob, error: fetchError } = await supabase
      .from("jobs")
      .select("id, external_calendar_event_id")
      .eq("id", jobId)
      .eq("tenant_id", tenantId)
      .single();

    if (fetchError) {
      if (import.meta.env.DEV) console.warn("Failed to fetch job for calendar cleanup:", fetchError);
      // Continue with status update even if fetch fails
    } else if (existingJob?.external_calendar_event_id) {
      // Delete the calendar event
      try {
        const response = await supabase.functions.invoke("delete-calendar-event", {
          body: {
            tenant_id: tenantId,
            event_id: existingJob.external_calendar_event_id,
            job_id: jobId,
          },
        });

        if (response.error) {
          if (import.meta.env.DEV) console.warn("Failed to delete calendar event on cancel:", response.error);
          // Continue with status update - don't block cancellation
        } else {
          if (import.meta.env.DEV) console.log(`HIGH-12: Deleted calendar event ${existingJob.external_calendar_event_id} for cancelled job ${jobId}`);
        }
      } catch (calendarError) {
        if (import.meta.env.DEV) console.warn("Calendar event deletion failed on cancel:", calendarError);
        // Continue - don't block job cancellation if calendar fails
      }
    }
  }

  const updates: Record<string, unknown> = {
    status,
    updated_at: new Date().toISOString(),
  };

  // Set timestamps based on status
  if (status === "en_route") {
    updates.en_route_at = new Date().toISOString();
  } else if (status === "in_progress") {
    updates.arrived_at = new Date().toISOString();
  } else if (status === "completed") {
    updates.completed_at = new Date().toISOString();
  } else if (status === "cancelled") {
    // HIGH-12: Clear calendar event ID since we deleted it
    updates.external_calendar_event_id = null;
    updates.cancelled_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from("jobs")
    .update(updates)
    .eq("id", jobId)
    .eq("tenant_id", tenantId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function assignTechnician(
  tenantId: string,
  jobId: string,
  technicianId: string | null
): Promise<Job> {
  if (!supabase) throw new Error("Supabase client not initialized");

  const { data, error } = await supabase
    .from("jobs")
    .update({
      technician_id: technicianId,
      status: technicianId ? "scheduled" : "unassigned",
      updated_at: new Date().toISOString(),
    })
    .eq("id", jobId)
    .eq("tenant_id", tenantId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function deleteJob(tenantId: string, jobId: string): Promise<void> {
  if (!supabase) throw new Error("Supabase client not initialized");

  // 1. First fetch the job to get external_calendar_event_id
  const { data: job, error: fetchError } = await supabase
    .from("jobs")
    .select("id, external_calendar_event_id")
    .eq("id", jobId)
    .eq("tenant_id", tenantId)
    .single();

  if (fetchError) throw fetchError;

  // 2. If job has a linked calendar event, delete it from Google Calendar
  if (job?.external_calendar_event_id) {
    try {
      const response = await supabase.functions.invoke("delete-calendar-event", {
        body: {
          tenant_id: tenantId,
          event_id: job.external_calendar_event_id,
          job_id: jobId,
        },
      });

      if (response.error) {
        if (import.meta.env.DEV) console.warn("Failed to delete calendar event:", response.error);
        // Continue with job deletion even if calendar deletion fails
        // The calendar event becomes orphaned but job can still be deleted
      }
    } catch (calendarError) {
      if (import.meta.env.DEV) console.warn("Calendar event deletion failed:", calendarError);
      // Continue - don't block job deletion if calendar fails
    }
  }

  // 3. Delete the job record
  const { error } = await supabase
    .from("jobs")
    .delete()
    .eq("id", jobId)
    .eq("tenant_id", tenantId);

  if (error) throw error;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch jobs for the current tenant.
 */
export function useJobs(filters?: JobFilters) {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...JOBS_QUERY_KEY, tenant?.id, filters],
    queryFn: () => fetchJobs(tenant!.id, filters),
    enabled: !!tenant?.id,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch jobs for a specific technician.
 */
export function useTechnicianJobs(
  technicianId?: string,
  dateFrom?: string,
  dateTo?: string
) {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...JOBS_QUERY_KEY, "technician", technicianId, dateFrom, dateTo],
    queryFn: () => fetchTechnicianJobs(tenant!.id, technicianId!, dateFrom, dateTo),
    enabled: !!tenant?.id && !!technicianId,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch a single job by ID.
 * Includes tenant_id filter for proper multi-tenant isolation.
 */
export function useJob(jobId?: string) {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...JOBS_QUERY_KEY, tenant?.id, jobId],
    queryFn: () => fetchJobById(tenant!.id, jobId!),
    enabled: !!tenant?.id && !!jobId,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch today's jobs for the current tenant.
 */
export function useTodaysJobs() {
  const { data: tenant } = useCurrentTenant();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return useQuery({
    queryKey: [...JOBS_QUERY_KEY, "today", tenant?.id],
    queryFn: () =>
      fetchJobs(tenant!.id, {
        dateFrom: today.toISOString(),
        dateTo: tomorrow.toISOString(),
      }),
    enabled: !!tenant?.id,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch jobs for a specific customer.
 */
export function useCustomerJobs(customerId?: string) {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...JOBS_QUERY_KEY, "customer", customerId],
    queryFn: () => fetchJobs(tenant!.id, { customerId }),
    enabled: !!tenant?.id && !!customerId,
    staleTime: 30000,
  });
}

/**
 * Hook to create a new job.
 * Automatically finds or creates a customer record based on phone number.
 * Applies dispatch auto-assignment logic (single tech = auto-assign, multi-tech = suggestion).
 */
export function useCreateJob() {
  const queryClient = useQueryClient();
  const { data: tenant } = useCurrentTenant();

  return useMutation({
    mutationFn: (input: CreateJobInput) => createJob(tenant!.id, input),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });

      // Also invalidate customers if we created or linked one
      if (result.customerLinked) {
        queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });
      }

      // Invalidate dispatch suggestions if one was created
      if (result.dispatchResult?.suggestionCreated) {
        queryClient.invalidateQueries({ queryKey: ["dispatch-suggestions"] });
      }

      // Build description based on what happened
      const descriptions: string[] = [];

      if (result.customerLinked && result.customerIsNew) {
        descriptions.push("New customer created");
      } else if (result.customerLinked) {
        descriptions.push("Linked to existing customer");
      }

      if (result.dispatchResult?.autoAssigned) {
        descriptions.push("Auto-assigned to technician");
      } else if (result.dispatchResult?.suggestionCreated) {
        descriptions.push("Pending dispatch approval");
      }

      toast.success("Job created", {
        description: descriptions.length > 0 ? descriptions.join(" • ") : undefined,
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to create job", {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to update a job.
 */
export function useUpdateJob() {
  const queryClient = useQueryClient();
  const { data: tenant } = useCurrentTenant();

  return useMutation({
    mutationFn: ({ jobId, updates }: { jobId: string; updates: UpdateJobInput }) =>
      updateJob(tenant!.id, jobId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
      toast.success("Job updated successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to update job", {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to update job status with optimistic updates.
 * Immediately reflects the new status in the UI, rolling back on error.
 */
export function useUpdateJobStatus() {
  const queryClient = useQueryClient();
  const { data: tenant } = useCurrentTenant();

  return useMutation({
    mutationFn: ({ jobId, status }: { jobId: string; status: JobStatus }) =>
      updateJobStatus(tenant!.id, jobId, status),
    onMutate: async ({ jobId, status }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: JOBS_QUERY_KEY });

      // Snapshot all job query caches for rollback
      const previousQueries = queryClient.getQueriesData<Job[]>({ queryKey: JOBS_QUERY_KEY });

      // Optimistically update all matching job lists in cache
      queryClient.setQueriesData<Job[]>(
        { queryKey: JOBS_QUERY_KEY },
        (old) => {
          if (!old || !Array.isArray(old)) return old;
          return old.map((job) =>
            job.id === jobId ? { ...job, status, updated_at: new Date().toISOString() } : job
          );
        }
      );

      return { previousQueries };
    },
    onError: (error: Error, _variables, context) => {
      // Rollback all caches to previous state
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error("Failed to update status", {
        description: error.message,
      });
    },
    onSuccess: (_, { status }) => {
      const messages: Record<JobStatus, string> = {
        unassigned: "Job unassigned",
        scheduled: "Job scheduled",
        confirmed: "Customer confirmed",
        en_route: "En route to customer",
        in_progress: "Job started",
        completed: "Job completed!",
        cancelled: "Job cancelled",
      };
      toast.success(messages[status] || "Status updated");
    },
    onSettled: () => {
      // Always refetch after mutation to sync with server
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    },
  });
}

/**
 * Hook to assign a technician to a job with optimistic updates.
 */
export function useAssignTechnician() {
  const queryClient = useQueryClient();
  const { data: tenant } = useCurrentTenant();

  return useMutation({
    mutationFn: ({
      jobId,
      technicianId,
    }: {
      jobId: string;
      technicianId: string | null;
    }) => assignTechnician(tenant!.id, jobId, technicianId),
    onMutate: async ({ jobId, technicianId }) => {
      await queryClient.cancelQueries({ queryKey: JOBS_QUERY_KEY });

      const previousQueries = queryClient.getQueriesData<Job[]>({ queryKey: JOBS_QUERY_KEY });

      queryClient.setQueriesData<Job[]>(
        { queryKey: JOBS_QUERY_KEY },
        (old) => {
          if (!old || !Array.isArray(old)) return old;
          return old.map((job) =>
            job.id === jobId
              ? {
                  ...job,
                  technician_id: technicianId,
                  status: technicianId ? "scheduled" as JobStatus : "unassigned" as JobStatus,
                  updated_at: new Date().toISOString(),
                }
              : job
          );
        }
      );

      return { previousQueries };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error("Failed to assign technician", {
        description: error.message,
      });
    },
    onSuccess: (_, { technicianId }) => {
      toast.success(
        technicianId ? "Technician assigned" : "Technician unassigned"
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    },
  });
}

/**
 * Hook to delete a job with optimistic removal from lists.
 */
export function useDeleteJob() {
  const queryClient = useQueryClient();
  const { data: tenant } = useCurrentTenant();

  return useMutation({
    mutationFn: (jobId: string) => deleteJob(tenant!.id, jobId),
    onMutate: async (jobId) => {
      await queryClient.cancelQueries({ queryKey: JOBS_QUERY_KEY });

      const previousQueries = queryClient.getQueriesData<Job[]>({ queryKey: JOBS_QUERY_KEY });

      queryClient.setQueriesData<Job[]>(
        { queryKey: JOBS_QUERY_KEY },
        (old) => {
          if (!old || !Array.isArray(old)) return old;
          return old.filter((job) => job.id !== jobId);
        }
      );

      return { previousQueries };
    },
    onError: (error: Error, _jobId, context) => {
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error("Failed to delete job", {
        description: error.message,
      });
    },
    onSuccess: () => {
      toast.success("Job deleted");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    },
  });
}

// ============================================================================
// Stats Hooks
// ============================================================================

/**
 * Hook to get job statistics for the dashboard.
 */
export function useJobStats() {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...JOBS_QUERY_KEY, "stats", tenant?.id],
    queryFn: async () => {
      if (!supabase || !tenant?.id) return null;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Get today's jobs with status counts
      const { data: jobs, error } = await supabase
        .from("jobs")
        .select("status")
        .eq("tenant_id", tenant.id)
        .gte("scheduled_at", today.toISOString())
        .lt("scheduled_at", tomorrow.toISOString());

      if (error) throw error;

      const stats = {
        total: jobs?.length || 0,
        scheduled: 0,
        in_progress: 0,
        completed: 0,
        unassigned: 0,
        en_route: 0,
      };

      jobs?.forEach((job) => {
        const status = job.status as JobStatus;
        if (status && stats[status] !== undefined) {
          stats[status]++;
        }
      });

      return stats;
    },
    enabled: !!tenant?.id,
    staleTime: 60000,
  });
}

// ============================================================================
// Realtime Subscription Hooks
// ============================================================================

/**
 * Hook to subscribe to real-time job updates for the current tenant.
 * Automatically invalidates relevant queries when jobs are created, updated, or deleted.
 *
 * Usage: Call this hook in dashboard components to enable live updates.
 *
 * @example
 * function Dashboard() {
 *   useJobsRealtime(); // Enable live updates
 *   const { data: jobs } = useJobs(); // Will auto-refresh on changes
 *   return <JobList jobs={jobs} />;
 * }
 */
export function useJobsRealtime() {
  const { data: tenant } = useCurrentTenant();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!tenant?.id || !supabase) return;

    const channel = supabase
      .channel(`jobs:${tenant.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "jobs",
          filter: `tenant_id=eq.${tenant.id}`,
        },
        (payload) => {
          if (import.meta.env.DEV) console.log("[Realtime] Job change:", payload.eventType);

          // Invalidate all job-related queries
          queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });

          // Also invalidate schedule queries since they depend on jobs
          queryClient.invalidateQueries({ queryKey: ["schedule"] });
        }
      )
      .subscribe((status) => {
        if (import.meta.env.DEV) console.log("[Realtime] Jobs subscription status:", status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenant?.id, queryClient]);
}
